import { motion } from 'framer-motion';
import { Sun, ArrowUpRight, Target, ListChecks, Quote } from 'lucide-react';
import { useEnquiry } from '../context/EnquiryContext';
import { COMPANY } from '../data/site';

const MISSION = [
  'Provide high-quality construction and infrastructure services',
  'Ensure timely execution with superior workmanship',
  'Expand across multiple cities through strategic growth',
  'Promote sustainable development through solar energy',
  'Build long-term relationships through transparency and reliability',
];

const USP = [
  { num: '01', title: 'Integrated Infrastructure Partner', desc: 'Construction services and material supply under one platform — quality control, cost optimisation and streamlined execution.' },
  { num: '02', title: 'Sustainability Focus', desc: 'Solar-ready layouts and renewable energy solutions that let customers adopt cleaner, energy-efficient living.' },
  { num: '03', title: 'Customer-Centric Execution', desc: 'Customised project planning, transparent communication and delivery dates we keep.' },
  { num: '04', title: 'Cost-Efficient Delivery', desc: 'Optimised procurement and project management that cut costs without cutting quality.' },
];

const SOLAR = ['Solar Power System Installation', 'Rooftop Solar Solutions', 'Solar Panel Supply', 'Solar EPC Services', 'Solar Infrastructure Development', 'Energy Optimization Solutions'];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, delay },
});

export default function About() {
  const { open } = useEnquiry();
  return (
    <main data-testid="about-page">
      <section className="pt-40 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">ABOUT US — {COMPANY.name.toUpperCase()}</p>
          <motion.h1
            initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-ink max-w-5xl"
            data-testid="about-headline"
          >
            Build Today.<br /><span className="font-serif italic font-medium text-terra">Sustain</span> Tomorrow.
          </motion.h1>
          <motion.p {...fade(0.15)} className="mt-10 text-base lg:text-lg text-ink/60 leading-relaxed max-w-3xl" data-testid="about-story">
            {COMPANY.name} is an emerging infrastructure and construction services company delivering quality residential,
            commercial and infrastructure development — building construction, civil contracting and construction material
            supply. With a vision to diversify into renewable energy, we are expanding into solar project development and
            sustainable infrastructure, creating durable, cost-effective and environmentally responsible projects.
          </motion.p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-stone border-y border-clay" data-testid="about-vision-mission">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-clay border border-clay">
          <motion.div {...fade(0)} className="bg-sand p-10 lg:p-14">
            <span className="w-11 h-11 bg-moss text-sand flex items-center justify-center mb-8"><Target size={18} /></span>
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">OUR VISION</p>
            <p className="font-heading font-bold tracking-tight text-2xl lg:text-3xl text-ink leading-snug">
              To become a trusted and innovative infrastructure company delivering sustainable construction and renewable
              energy solutions — for smarter cities, greener communities and a better quality of life.
            </p>
          </motion.div>
          <motion.div {...fade(0.1)} className="bg-sand p-10 lg:p-14">
            <span className="w-11 h-11 bg-terra text-white flex items-center justify-center mb-8"><ListChecks size={18} /></span>
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">OUR MISSION</p>
            <ul className="space-y-4">
              {MISSION.map((m) => (
                <li key={m} className="flex gap-3 text-sm lg:text-base text-ink/70 leading-relaxed">
                  <span className="w-1.5 h-1.5 bg-terra mt-2 shrink-0" /> {m}.
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="about-usp">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">WHY PRAKRUTI — OUR USP</p>
          <h2 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl text-ink mb-16">One partner,<br />end to end</h2>
          <div className="border-t border-clay">
            {USP.map((u, i) => (
              <motion.div key={u.num} {...fade(i * 0.06)} className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline py-9 border-b border-clay" data-testid={`usp-${u.num}`}>
                <span className="md:col-span-2 font-serif italic text-5xl lg:text-7xl text-ink/15 group-hover:text-terra transition-colors duration-500 leading-none">{u.num}</span>
                <h3 className="md:col-span-5 font-heading font-bold text-xl lg:text-2xl tracking-tight text-ink">{u.title}</h3>
                <p className="md:col-span-5 text-sm lg:text-base leading-relaxed text-ink/60">{u.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-moss text-sand" data-testid="about-solar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-4">THE NEXT CHAPTER — RENEWABLE ENERGY</p>
            <motion.h2 {...fade(0)} className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl leading-tight">
              Prakruti goes <span className="font-serif italic font-medium text-terra">solar</span>
            </motion.h2>
            <motion.p {...fade(0.1)} className="mt-6 text-sand/60 leading-relaxed max-w-lg">
              Rising energy costs shouldn't decide how India builds. Our upcoming renewable energy division brings solar
              installation, equipment supply and EPC services to the same roof as our construction work — so every home we
              hand over can power itself.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-sand/10 border border-sand/10">
            {SOLAR.map((s, i) => (
              <motion.div key={s} {...fade(i * 0.05)} className="bg-moss p-6 flex items-start gap-3" data-testid={`solar-item-${i}`}>
                <Sun size={15} className="text-terra shrink-0 mt-0.5" />
                <span className="text-sm text-sand/80 leading-snug">{s}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="about-founder">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4">
            <div className="aspect-[4/5] bg-stone border border-clay flex items-center justify-center">
              <span className="font-serif italic text-8xl text-terra/40">RR</span>
            </div>
          </div>
          <div className="lg:col-span-8">
            <Quote size={36} className="text-terra/30 mb-6" />
            <motion.blockquote {...fade(0)} className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-ink max-w-2xl">
              “Every plot we layout and every wall we raise must outlive us. That is the only way to lead a better future.”
            </motion.blockquote>
            <motion.div {...fade(0.1)} className="mt-8">
              <p className="font-heading font-bold text-lg tracking-tight text-ink" data-testid="founder-name">{COMPANY.owner}</p>
              <p className="text-sm text-ink/50 mt-1">Founder & Managing Director, {COMPANY.name}</p>
              <p className="text-xs text-ink/40 mt-3 tracking-[0.18em]">FOCUS REGIONS — KARNATAKA · TELANGANA · ANDHRA PRADESH · MAHARASHTRA</p>
            </motion.div>
            <motion.button {...fade(0.15)} onClick={open} data-testid="about-enquire-btn"
              className="mt-10 bg-moss text-sand text-[11px] font-medium tracking-[0.2em] px-10 py-4 flex items-center gap-2 hover:bg-terra transition-colors duration-300">
              WORK WITH US <ArrowUpRight size={14} />
            </motion.button>
          </div>
        </div>
      </section>
    </main>
  );
}
